import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShieldAlert, Check, X, Clock, User, Calendar, FileText } from 'lucide-react';
import { fmtDateTimeIST } from '../lib/tz';


const MODULE_LABELS = {
  inventory_closing: 'Inventory Closing',
  daily_entry: 'Daily Entry',
  marketing: 'Marketing',
};

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, label: 'Pending' },
  approved: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check, label: 'Approved' },
  rejected: { color: 'bg-red-50 text-red-700 border-red-200', icon: X, label: 'Rejected' },
};

export default function AdminOverrideRequests() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = tab === 'all' ? '' : tab;
      const q = status ? `?status=${status}` : '';
      const res = await axios.get(`${API}/admin/override-requests${q}`, { headers: getHeaders() });
      setRequests(res.data);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, [API, getHeaders, tab]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    setProcessing(p => ({ ...p, [id]: action }));
    try {
      await axios.post(`${API}/admin/override-requests/${id}/${action}`, {}, { headers: getHeaders() });
      toast.success(`Request ${action}d`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
    finally { setProcessing(p => ({ ...p, [id]: null })); }
  };

  return (
    <div className="space-y-4" data-testid="admin-override-requests-page">
      <div>
        <h1 className="text-xl font-bold text-[#1A2421] flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-600" /> Override Requests
        </h1>
        <p className="text-sm text-[#5C6B64] mt-0.5">Review and approve manager override requests</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F3F5F2] border border-[#E2E8DF]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-[color:var(--color-primary)] text-sm"
            data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white data-[state=active]:text-[color:var(--color-primary)] text-sm"
            data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-white data-[state=active]:text-[color:var(--color-primary)] text-sm"
            data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:text-[color:var(--color-primary)] text-sm"
            data-testid="tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-[#5C6B64] text-center py-8" data-testid="no-requests">No {tab !== 'all' ? tab : ''} requests</p>
          ) : (
            <div className="space-y-3" data-testid="requests-list">
              {requests.map(r => {
                const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusCfg.icon;
                return (
                  <Card key={r.id} className="border-[#E2E8DF] shadow-sm" data-testid={`request-${r.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <span className="text-sm font-semibold text-[#1A2421]">{r.outlet_name}</span>
                            <Badge variant="outline" className="text-[10px] border-[#E2E8DF]">
                              {MODULE_LABELS[r.module] || r.module}
                              {r.section ? ` — ${r.section}` : ''}
                            </Badge>
                            <Badge className={`text-[9px] ${statusCfg.color}`}>
                              <StatusIcon className="w-2.5 h-2.5 mr-0.5" /> {statusCfg.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-[#5C6B64] mb-2">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.date}</span>
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.requested_by}</span>
                            <span>{fmtDateTimeIST(r.requested_at)}</span>
                          </div>
                          <div className="flex items-start gap-1.5 bg-[#F9F9F8] rounded-lg p-2.5">
                            <FileText className="w-3.5 h-3.5 text-[#5C6B64] mt-0.5 shrink-0" />
                            <p className="text-xs text-[#1A2421]">{r.reason}</p>
                          </div>
                          {r.approved_by && (
                            <p className="text-[10px] text-emerald-600 mt-1.5">Approved by {r.approved_by} at {fmtDateTimeIST(r.approved_at)}</p>
                          )}
                          {r.rejected_by && (
                            <p className="text-[10px] text-red-600 mt-1.5">Rejected by {r.rejected_by} at {fmtDateTimeIST(r.rejected_at)}</p>
                          )}
                        </div>
                        {r.status === 'pending' && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" onClick={() => handleAction(r.id, 'approve')}
                              disabled={!!processing[r.id]}
                              className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                              data-testid={`approve-${r.id}`}>
                              {processing[r.id] === 'approve' ? '...' : <><Check className="w-3.5 h-3.5 mr-1" /> Approve</>}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleAction(r.id, 'reject')}
                              disabled={!!processing[r.id]}
                              className="h-8 px-3 border-red-200 text-red-600 hover:bg-red-50 text-xs"
                              data-testid={`reject-${r.id}`}>
                              {processing[r.id] === 'reject' ? '...' : <><X className="w-3.5 h-3.5 mr-1" /> Reject</>}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
